import 'dayjs/locale/en';
import 'dayjs/locale/ko';
import dayjs, { ConfigType, Dayjs, OpUnitType, QUnitType } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import minMax from 'dayjs/plugin/minMax';
import { range } from 'ramda';
import { isValidDate } from 'ramda-adjunct';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(minMax);
dayjs.locale('ko');
dayjs.locale('en');

export const ONE_MONTH_IN_MS = 3000 * 24 * 60 * 60 * 1000;

export const SUPPORT_FORMAT_DATE_LIST = [
	undefined as unknown as string,
	'x',
	'YYYYMMDD',
	'YYYYMMDDHHmmss',
	'YYYY.MM.DD HH:mm',
	'YYYY.MM.DD',
	'YYYY.MM',
	'YYYY-MM-DDTHH:mm',
	'YYYY-MM-DD HH:mm:ss',
	'YYYY-MM-DD HH:mm',
	'YYYY-MM-DD',
	'YY/MM/DD',
	'HH      mm',
	'HH       mm',
	'YYYY     MM     DD',
	'YYYY       MM        DD',
	'YYYY        MM         DD',
	'YYYY         MM         DD',
	'YYYY          MM           DD',
	'YYYY           MM             DD',
	'YYYY          MM               DD',
	'YYYY                MM              DD',
	'YYYY                     MM                          DD',
	'YYYY                          MM                           DD',
	'YYYY                           MM                                 DD',
	'YYYY              MM                DD',
	'YYYY           MM        DD       HH     mm',
	'YYYY                          MM                          DD',
	'YYYY                           MM                                DD',
	'YYYY              MM             DD',
	'HH:mm:ss',
	'HH:mm',
	'dddd',
	'                  YYYY              MM            DD',
	'YYYY            MM         DD',
];

export const formatDate = (
	date: string | number | null | undefined | Dayjs | Date,
	options?: { inputFormat?: string; outputFormat?: string }
) => {
	if (!date) return '';

	const {
		inputFormat = SUPPORT_FORMAT_DATE_LIST,
		outputFormat = 'YYYY-MM-DD',
	} = options || {};

	return typeof date === 'number'
		? dayjs.unix(date).format(outputFormat)
		: dayjs(date, inputFormat).format(outputFormat);
};

export const formatDateCard = (date: Date, globalTime?: string) => {
	if (!isValidDate(date)) return date;
	let text = formatDate(`${formatDate(date)} ${globalTime}`, {
		outputFormat: 'YYYY-MM-DD (ddd) HH:mm',
	});
	return text;
};

export const diffDate = (
	date1: ConfigType = dayjs(),
	date2: ConfigType = dayjs(),
	unit: QUnitType | OpUnitType = 'day'
): number => {
	if (!date1 || !date2) return 0;
	const _date1 = formatDate(date1);
	const _date2 = formatDate(date2);
	return dayjs(_date1).diff(_date2, unit);
};

export const betweenSevenDaysAgo = (date: string): boolean => {
	const sevenDays = dayjs().subtract(7, 'day');
	return dayjs(date).isBetween(sevenDays, dayjs(), null, '[]');
};

export const getYear = (date?: Date): string => {
	return dayjs(date).year().toString();
};

export const getMonth = (date?: Date): string => {
	return (dayjs(date).month() + 1).toString();
};

export const getStartOfWeek = (date?: Date): string => {
	const currentDate = dayjs(date);

	const startOfFirstWeek = currentDate.startOf('week');

	return formatDate(startOfFirstWeek);
};

export const getEndOfWeek = (date?: Date) => {
	const currentDate = dayjs(date);

	const endOfLastWeek = currentDate.endOf('week');

	return formatDate(endOfLastWeek);
};

export const getStartOfFirstWeek = (date?: Date): string => {
	const currentDate = dayjs(date);

	const startOfMonth = currentDate.startOf('month');

	const startOfFirstWeek = startOfMonth.startOf('week');

	return formatDate(startOfFirstWeek);
};

export const getEndOfLastWeek = (date?: Date) => {
	const currentDate = dayjs(date);

	const endOfMonth = currentDate.endOf('month');

	const endOfLastWeek = endOfMonth.endOf('week');

	return formatDate(endOfLastWeek);
};

export const getStartOfMonth = (date?: Date) => {
	const currentDate = dayjs(date);

	const startOfMonth = currentDate.startOf('month');

	return formatDate(startOfMonth);
};

export const getEndOfMonth = (date?: Date) => {
	const currentDate = dayjs(date);

	const endOfMonth = currentDate.endOf('month');

	return formatDate(endOfMonth);
};

export const getDisabledSatSunDay = (date?: Date) => {
	let currentDay = dayjs(getStartOfFirstWeek(date));
	const endOfLastWeek = dayjs(getEndOfLastWeek(date));
	const daysInCalendar = [];

	while (
		currentDay.isBefore(endOfLastWeek) ||
		currentDay.isSame(endOfLastWeek, 'day')
	) {
		const dayOfWeek = currentDay.day();
		if (dayOfWeek === 0 || dayOfWeek === 6) {
			daysInCalendar.push(formatDate(currentDay));
		}
		currentDay = currentDay.add(1, 'day');
	}

	return daysInCalendar;
};

export const calculateMinimumRelaxTime = (
	startTime?: string,
	endTime?: string
): number | null => {
	if (!startTime || !endTime) return null;

	const start = dayjs(startTime, 'HH:mm');
	const end = dayjs(endTime, 'HH:mm');

	if (!start.isValid() || !end.isValid()) return null;

	const workingHours = end.diff(start, 'hour', true);

	if (workingHours <= 0) return 0;

	const minimumRelaxTime = Math.floor(workingHours / 4) * 0.5;

	return minimumRelaxTime;
};

export const getDisableTimeOnly = (
	current: Dayjs,
	time: Dayjs | string | null,
	isStart: boolean
) => {
	const totalHour = 24;
	const compareHour = dayjs(time, 'HH:mm').hour();
	const compareMinute = dayjs(time, 'HH:mm').minute();

	const disabledHours = () => {
		if (!time) return [];

		if (isStart) {
			return range(compareHour + 1, totalHour);
		} else {
			return range(0, compareHour);
		}
	};

	const disabledMinutes = () => {
		if (!time) return [];

		if (isStart) {
			return current.hour() === compareHour ? range(compareMinute, 60) : [];
		} else {
			return current.hour() === compareHour ? range(0, compareMinute + 1) : [];
		}
	};

	return {
		disabledHours,
		disabledMinutes,
	};
};

export const formatDateStringOrUnix = (
	date: string | number | null | undefined | Dayjs | Date,
	options?: { inputFormat?: string; outputFormat?: string }
) => {
	if (typeof date === 'number') {
		return formatDate(dayjs.unix(date), options);
	}

	return formatDate(date, options);
};

export const disabledDateStartDate = (
	current: Dayjs,
	date: string,
	isDisableDayBeforNow?: boolean
) => {
	return (
		(isDisableDayBeforNow && current < dayjs().startOf('day')) ||
		(!!date &&
			current &&
			current > dayjs(date, SUPPORT_FORMAT_DATE_LIST).endOf('day'))
	);
};

export const disabledDateEndDate = (
	current: Dayjs,
	date: string,
	isDisableDayBeforNow?: boolean
) => {
	return (
		(isDisableDayBeforNow && current < dayjs().startOf('day')) ||
		(!!date &&
			current &&
			current < dayjs(date, SUPPORT_FORMAT_DATE_LIST).startOf('day'))
	);
};

export const disabledRangeTimeFrom = (date: string) => {
	const disabledHours = () => {
		if (!date) return [];
		return range(0, 60).splice(dayjs(date, 'HH:mm').hour() + 1);
	};

	const disabledMinutes = (hour: any) => {
		if (hour === dayjs(date, 'HH:mm').hour()) {
			return range(0, 60).splice(dayjs(date, 'HH:mm').minute() + 1);
		}

		return [];
	};

	return {
		disabledHours,
		disabledMinutes,
	};
};

export const disabledRangeTimeTo = (date: string) => {
	const disabledHours = () => {
		if (!date) return [];
		return range(0, 60).splice(0, dayjs(date, 'HH:mm').hour());
	};

	const disabledMinutes = (hour: any) => {
		if (hour === dayjs(date, 'HH:mm').hour()) {
			return range(0, 60).splice(0, dayjs(date, 'HH:mm').minute());
		}

		return [];
	};

	return {
		disabledHours,
		disabledMinutes,
	};
};

export const getDisabledTime = (
	current: Dayjs,
	compareDate: Dayjs,
	isStart: boolean
) => {
	const isToday = current.isToday();
	const isCurrentDate = formatDate(compareDate) === formatDate(current);
	const now = dayjs();

	const disabledHours = () => {
		if (!isToday && !isCurrentDate) return [];
		if (isStart) {
			if (!isToday) return range(compareDate.hour() + 1, 24);
			if (!isCurrentDate) return range(0, now.hour());
			return [...range(0, now.hour()), ...range(compareDate.hour() + 1, 24)];
		} else {
			if (!isCurrentDate) return range(0, now.hour());
			return range(0, compareDate.hour());
		}
	};

	const disabledMinutes = () => {
		if (!isToday && !isCurrentDate) return [];
		if (isStart) {
			if (!isToday && current.hour() === compareDate.hour())
				return range(compareDate.minute() + 1, 60);
			if (!isCurrentDate && current.hour() === now.hour())
				return range(0, now.minute());
			if (
				current.hour() === now.hour() &&
				current.hour() === compareDate.hour()
			)
				return [
					...range(0, now.minute()),
					...range(compareDate.minute() + 1, 60),
				];
			if (current.hour() === now.hour()) return range(0, now.minute());
			if (current.hour() === compareDate.hour())
				return range(compareDate.minute() + 1, 60);
		} else {
			if (!isCurrentDate && current.hour() === now.hour())
				return range(0, now.minute());
			if (current.hour() === compareDate.hour())
				return range(0, compareDate.minute());
		}
		return [];
	};

	return { disabledHours, disabledMinutes };
};

export const getDiffExpirationDate = (date?: string | number) => {
	if (!date) return -1;
	const outputFormat = { outputFormat: 'YYYY-MM-DD' };
	const dateFormat = formatDate(date, outputFormat);
	const toDateFormat = formatDate(dayjs(), outputFormat);

	return dayjs(dateFormat, SUPPORT_FORMAT_DATE_LIST).diff(
		dayjs(toDateFormat, SUPPORT_FORMAT_DATE_LIST),
		'days'
	);
};

export const disabledBirthday = (currentDate: Dayjs | null): boolean => {
	if (!currentDate) return false;

	const today = dayjs();
	return currentDate.isAfter(today.endOf('day'));
};

export const isWithinRangeDays = (
	startRange: string,
	currentDay: string,
	endRange: string
) => {
	return (
		dayjs(startRange).isBefore(dayjs(currentDay)) &&
		dayjs(currentDay).isBefore(dayjs(endRange))
	);
};

export const getDiffExpirationDateString = (date?: string | number) => {
	if (!date) return '';

	const diffExpirationDate = getDiffExpirationDate(date);

	if (diffExpirationDate < 0) return formatDate(date);

	return `[D-${diffExpirationDate}] ${formatDate(date)}`;
};

export { dayjs };
